import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

import Section from '../../../layout/Section';
import Button from '../../../common/inputs/Button';
import EditableTable, { EditableTableColumn, EditableTableRef } from '../../../tables/common/EditableTable';
import { EditableTableConfig } from '../../../tables/common/types';
import {
  Shareholder,
  ShareholderCreate,
  ShareholderUpdate,
  CompanyWithShareholders,
} from '../../../../types';
import { formatNumber } from '../../../../utils/formatters';
import { colors } from '../../../../styles/colors';
import { companiesApi } from '../../../../services/api/companies';

interface ShareholdersSectionProps {
  shareholders: Shareholder[];
  totalPercent: number;
  remainingPercent: number;
  canEdit: boolean;
  isSaving: boolean;
  error: string | null;
  onCreateShareholder: (data: ShareholderCreate) => Promise<CompanyWithShareholders>;
  onUpdateShareholder: (shareholderId: string, data: ShareholderUpdate) => Promise<CompanyWithShareholders>;
  onDeleteShareholder: (shareholderId: string) => Promise<void>;
  onDataChanged?: () => void;
}

const ShareholdersSection: React.FC<ShareholdersSectionProps> = ({
  shareholders,
  totalPercent,
  remainingPercent,
  canEdit,
  isSaving,
  error,
  onCreateShareholder,
  onUpdateShareholder,
  onDeleteShareholder,
  onDataChanged,
}) => {
  const tableRef = useRef<EditableTableRef>(null);
  const [nameSuggestions, setNameSuggestions] = useState<string[]>([]);

  useEffect(() => {
    companiesApi.getKommanditistNameOptions().then(setNameSuggestions).catch(() => {});
  }, []);

  const columns: EditableTableColumn[] = useMemo(() => [
    {
      key: 'ist_eigene_beteiligung',
      label: 'Eigene Beteiligung',
      align: 'center' as const,
      width: '15%',
      sortable: false,
      editConfig: { type: 'checkbox' as const },
    },
    {
      key: 'name',
      label: 'Name',
      align: 'left' as const,
      width: '40%',
      sortable: true,
      editConfig: { type: 'autocomplete' as const, required: true, suggestions: nameSuggestions },
    },
    {
      key: 'anteil_prozent',
      label: 'Anteil',
      align: 'right' as const,
      width: '25%',
      sortable: true,
      editConfig: {
        type: 'number' as const,
        suffix: '%',
        required: true,
        allowDecimals: true,
        min: 0,
        max: 100,
      },
    },
  ], [nameSuggestions]);

  const editConfig: EditableTableConfig<Shareholder> = useMemo(() => ({
    fields: {
      ist_eigene_beteiligung: { type: 'checkbox' },
      name: { type: 'autocomplete', required: true, suggestions: nameSuggestions },
      anteil_prozent: { type: 'number', suffix: '%', required: true, allowDecimals: true, min: 0, max: 100 },
    },
    onCreate: async (data: Partial<Shareholder>) => {
      const result = await onCreateShareholder({
        name: data.name || '',
        anteil_prozent: data.anteil_prozent || 0,
        ist_eigene_beteiligung: !!data.ist_eigene_beteiligung,
      });
      const shareholders = result.shareholders ?? [];
      return shareholders[shareholders.length - 1] as Shareholder;
    },
    onUpdate: async (id: string, changes: Partial<Shareholder>) => {
      const updateData: Record<string, unknown> = {};
      if (changes.name !== undefined) updateData.name = changes.name;
      if (changes.anteil_prozent !== undefined) updateData.anteil_prozent = changes.anteil_prozent;
      if (changes.ist_eigene_beteiligung !== undefined) {
        updateData.ist_eigene_beteiligung = !!changes.ist_eigene_beteiligung;
      }
      const result = await onUpdateShareholder(id, updateData);
      return result.shareholders.find((sh) => sh.id === id) as Shareholder;
    },
    onDelete: async (id: string) => {
      await onDeleteShareholder(id);
    },
  }), [onCreateShareholder, onUpdateShareholder, onDeleteShareholder, nameSuggestions]);

  const renderCell = useCallback(
    (column: EditableTableColumn, _row: Shareholder, value: unknown) => {
      switch (column.key) {
        case 'name':
          return (
            <Typography variant="body2" sx={{
              fontWeight: "medium"
            }}>
              {value as string}
            </Typography>
          );
        case 'anteil_prozent':
          return (
            <Typography variant="body2" sx={{
              fontWeight: "medium"
            }}>
              {formatNumber(value as number, 2)}%
                          </Typography>
          );
        default:
          return undefined;
      }
    },
    [],
  );

  const handleAddShareholder = useCallback(() => {
    tableRef.current?.startCreating({
      name: '',
      anteil_prozent: remainingPercent,
      ist_eigene_beteiligung: false,
    });
  }, [remainingPercent]);

  const isComplete = Math.abs(totalPercent - 100) < 0.01;
  const isOverAllocated = totalPercent > 100.01;

  const sumColor = isComplete
    ? colors.status.success.main
    : isOverAllocated
      ? colors.status.error.main
      : colors.status.warning.main;

  return (
    <Section
      title="Kommanditisten"
      noPadding={true}
      hasWarning={shareholders.length > 0 && !isComplete}
      warningTooltip={isOverAllocated
        ? 'Die Summe der Anteile übersteigt 100%'
        : 'Die Summe der Anteile ergibt noch nicht 100%'
      }
      actions={
        canEdit ? (
          <Button
            variant="primary"
            startIcon={<AddIcon />}
            onClick={handleAddShareholder}
            disabled={isSaving}
          >
            Gesellschafter anlegen
          </Button>
        ) : undefined
      }
    >
      <EditableTable<Shareholder>
        ref={tableRef}
        columns={columns}
        data={shareholders}
        editConfig={editConfig}
        canEdit={canEdit}
        renderCell={renderCell}
        onDataChanged={onDataChanged}
        actionsColumnWidth="60px"
        actionsColumnAlign="right"
        variant="default"
        showBorder={false}
        emptyMessage="Es wurden noch keine Gesellschafter erfasst"
      />
      {error && (
        <Typography variant="body2" sx={{ color: colors.status.error.main, mt: 1, px: 2 }}>
          {error}
        </Typography>
      )}
    </Section>
  );
};

export default React.memo(ShareholdersSection);
